import { get } from 'lodash'
import {
  BasicCondition,
  Condition,
  ConditionDictionary,
  ConditionRange,
  ConditionTargetValue,
  ConditionValues,
  RefractionCondition,
  ResolvedCondition,
  NegationCondition,
  StrengthCondition,
  PriceCondition,
  RelationCondition,
  PriceValue,
} from '@ca/common/types'
import { useRemoteConfigStore } from 'hooks/useRemoteConfigStore'
import { debugConditions } from './getDebugLog'
import { RefractionUtils } from '@ca/common'

export const wrapNegation = (
  condition: ResolvedCondition | undefined,
  source: RelationCondition | undefined
) => {
  if (!condition) {
    return undefined
  }
  if (source?.negation) {
    return {
      selector: 'not',
      errorKey: source.errorKey,
      value: condition,
    } satisfies NegationCondition
  }
  return {
    ...condition,
    errorKey: source?.errorKey || condition.errorKey,
  }
}

export const resolveCondition = (
  conditionOrRef: Condition,
  dictionary: ConditionDictionary,
  stack?: string[]
): ResolvedCondition | undefined => {
  if (conditionOrRef.selector === 'relation') {
    const ref = conditionOrRef.value
    const res = dictionary[ref]
    if (stack?.includes(ref)) {
      debugConditions.warn(`Condition ${ref} refers to itself`)
      return undefined
    }

    if (res?.selector === 'relation') {
      return wrapNegation(resolveCondition(res, dictionary), conditionOrRef)
    }
    return wrapNegation(res, conditionOrRef)
  }
  return conditionOrRef
}

export const validateRange = (
  range: ConditionRange,
  value: number,
  withEquals?: boolean
) => {
  // This does an early return when condition is not met
  // We invert the condition so for range -2<=x<=2 we check if range[0] < -2
  const startValidation = withEquals ? basicValidators.lt : basicValidators.lte
  const endValidation = withEquals ? basicValidators.gt : basicValidators.gte

  if (range[0] !== null && startValidation(value, range[0])) {
    return false
  }
  if (range[1] !== null && endValidation(value, range[1])) {
    return false
  }
  return true
}

export const validateSingleSideRefractionCondition = (
  condition: RefractionCondition,
  values: {
    sph: number
    cyl: number
    total: number
    add: number
  }
) => {
  const { target } = condition
  const { withEquals, singleSide, ...ranges } = target
  return Object.keys(ranges).every((key) => {
    const range = ranges[key as keyof typeof ranges]

    if (
      range &&
      !validateRange(range, values[key as keyof typeof values], withEquals)
    ) {
      return false
    }
    return true
  })
}

export const validateSingleStrengthCondition = (
  condition: StrengthCondition,
  values: {
    sph: number
    cyl: number
  }
) => {
  const strength = RefractionUtils.getStrength(values)
  return validateRange(
    condition.target.range,
    strength,
    condition.target.withEquals
  )
}

export const validateStrengthCondition = (
  condition: StrengthCondition,
  values: ConditionValues
) => {
  const { ref } = values
  if (!ref || typeof ref !== 'object') {
    debugConditions.warn('Strength condition missing ref', {
      condition,
    })
    return {
      result: false,
      errorKey: condition.errorKey,
    }
  }
  const left = validateSingleStrengthCondition(condition, {
    sph: ref.l_sph,
    cyl: ref.l_cyl,
  })
  const right = validateSingleStrengthCondition(condition, {
    sph: ref.r_sph,
    cyl: ref.r_cyl,
  })
  debugConditions.log('Strength condition', {
    condition,
    left,
    right,
  })

  if (condition.target.singleSide) {
    const result = left || right
    return {
      result,
      errorKey: condition.errorKey,
      failurePath: result ? undefined : [{ selector: 'strength', errorKey: condition.errorKey }],
    }
  }

  const result = left && right
  return {
    result,
    errorKey: condition.errorKey,
    failurePath: result ? undefined : [{ selector: 'strength', errorKey: condition.errorKey }],
  }
}

export const validateRefractionCondition = (
  condition: RefractionCondition,
  values: ConditionValues
) => {
  const { ref } = values
  if (!ref || typeof ref !== 'object') {
    debugConditions.warn('Refraction condition missing ref', {
      condition,
    })
    return {
      result: false,
      errorKey: condition.errorKey,
    }
  }
  const left = validateSingleSideRefractionCondition(condition, {
    sph: ref.l_sph,
    cyl: ref.l_cyl,
    add: ref.l_add,
    total: RefractionUtils.getStrength({
      sph: ref.l_sph,
      cyl: ref.l_cyl,
    }),
  })

  const right = validateSingleSideRefractionCondition(condition, {
    sph: ref.r_sph,
    cyl: ref.r_cyl,
    add: ref.r_add,
    total: RefractionUtils.getStrength({
      sph: ref.r_sph,
      cyl: ref.r_cyl,
    }),
  })

  debugConditions.log('Refraction condition', {
    condition,
    left,
    right,
  })

  if (condition.target.singleSide) {
    const result = left || right
    return {
      result,
      errorKey: condition.errorKey,
      failurePath: result ? undefined : [{ selector: 'refraction', errorKey: condition.errorKey }],
    }
  }

  const result = left && right
  return {
    result,
    errorKey: condition.errorKey,
    failurePath: result ? undefined : [{ selector: 'refraction', errorKey: condition.errorKey }],
  }
}

export const basicValidators: Record<
  BasicCondition['selector'],
  (value: ConditionTargetValue, target: ConditionTargetValue) => boolean
> = {
  eq: (value, target) => {
    if (typeof value === 'string' && typeof target === 'string') {
      return value.toLowerCase() === target.toLowerCase()
    }
    // If target is false, we want to check if value is truthy
    if (target === false) {
      return !value
    }
    return value === target
  },
  neq: (value, target) => {
    if (typeof value === 'string' && typeof target === 'string') {
      return value.toLowerCase() !== target.toLowerCase()
    }
    return value !== target
  },
  gt: (value, target) => Number(value) > Number(target),
  lt: (value, target) => Number(value) < Number(target),
  gte: (value, target) => Number(value) >= Number(target),
  lte: (value, target) => Number(value) <= Number(target),
}

export const resolveTargetValue = (
  target: string | number | boolean,
  values: ConditionValues
) => {
  if (typeof target !== 'string') {
    return target
  }

  const resolved = get(values, target)

  if (
    typeof resolved === 'string' ||
    typeof resolved === 'number' ||
    typeof resolved === 'boolean'
  ) {
    return resolved
  }

  return target
}

const validateBasicCondition = (
  condition: BasicCondition,
  values: ConditionValues
) => {
  const { param, selector, target } = condition
  const value = get(values, param)
  const resolvedTarget = resolveTargetValue(target, values)

  if (typeof value === 'object') {
    debugConditions.warn(
      `Condition ${JSON.stringify(
        condition
      )} has an object value. This is not supported.`
    )
    return {
      result: false,
      errorKey: condition.errorKey,
    }
  }
  const result = basicValidators[selector](value, resolvedTarget)

  debugConditions.log('Basic condition', {
    condition,
    value,
    resolvedTarget,
    result,
  })
  return {
    result,
    errorKey: condition.errorKey,
    failurePath: result
      ? undefined
      : [{ selector: 'basic', errorKey: condition.errorKey, detail: { param, value, target: resolvedTarget, selector } }],
  }
}

const validatePriceCondition = (
  condition: PriceCondition,
  values: ConditionValues
) => {
  const region = useRemoteConfigStore.getState().config?.region

  if (!region) {
    return {
      result: false,
      errorKey: condition.errorKey,
    }
  }

  const { target } = condition
  const value = get(values, target.variable)

  const resolvedTarget = PriceValue.get(target.values, region)

  if (typeof value === 'object') {
    debugConditions.warn(
      `Condition ${JSON.stringify(
        condition
      )} has an object value. This is not supported.`
    )
    return {
      result: false,
      errorKey: condition.errorKey,
    }
  }
  const result = basicValidators[target.selector](value, resolvedTarget)
  debugConditions.log('Price condition', {
    condition,
    value,
    resolvedTarget,
    result,
  })
  return {
    result,
    errorKey: condition.errorKey,
    failurePath: result
      ? undefined
      : [{ selector: 'price', errorKey: condition.errorKey, detail: { param: target.variable, value, target: resolvedTarget, selector: target.selector } }],
  }
}

export type ConditionPathEntry = {
  id?: string
  selector: string
  errorKey?: string
  detail?: { param: string; value: unknown; target: unknown; selector: string }
}

export type ValidationResult = {
  result: boolean
  errorKey?: string
  failurePath?: ConditionPathEntry[]
}

const validateResolvedCondition = (
  condition: ResolvedCondition,
  values: ConditionValues,
  dictionary: ConditionDictionary
): ValidationResult => {
  if (condition.selector === 'and') {
    const checked = condition.value.map((condition) =>
      validateCondition(condition, values, dictionary)
    )
    const invalid = checked.find((result) => !result.result)

    return invalid
      ? {
          result: false,
          errorKey: condition.errorKey || invalid.errorKey,
          failurePath: [{ selector: 'and', errorKey: condition.errorKey }, ...(invalid.failurePath ?? [])],
        }
      : {
          result: true,
          errorKey: condition.errorKey,
        }
  }
  if (condition.selector === 'or') {
    const checked = condition.value.map((condition) =>
      validateCondition(condition, values, dictionary)
    )
    const isValid = checked.some((result) => result.result)
    const invalid = checked.find((result) => !result.result)
    return isValid
      ? {
          result: true,
        }
      : {
          result: false,
          errorKey: condition.errorKey || invalid?.errorKey,
          failurePath: [{ selector: 'or', errorKey: condition.errorKey }, ...(invalid?.failurePath ?? [])],
        }
  }
  if (condition.selector === 'refraction') {
    return validateRefractionCondition(condition, values)
  }

  if (condition.selector === 'strength') {
    return validateStrengthCondition(condition, values)
  }

  if (condition.selector === 'price') {
    return validatePriceCondition(condition, values)
  }

  if (condition.selector === 'not') {
    const inner = validateCondition(condition.value, values, dictionary)
    const result = !inner.result
    return {
      result,
      errorKey: condition.errorKey,
      failurePath: result
        ? undefined
        : [{ selector: 'not', errorKey: condition.errorKey }],
    }
  }

  // Not sure why TS is complaining here
  return validateBasicCondition(condition as BasicCondition, values)
}

/**
 * Resolve the condition and wrap with debug log
 */
function validateCondition(
  conditionOrRef: Condition,
  values: ConditionValues,
  dictionary: ConditionDictionary
): ValidationResult {
  const id = conditionOrRef.selector === 'relation' ? conditionOrRef.value : undefined
  const condition = resolveCondition(conditionOrRef, dictionary)
  if (!condition) {
    debugConditions.warn(`Condition ${conditionOrRef} not found in dictionary`)
    return {
      result: false,
      failurePath: [{ id, selector: 'unresolved' }],
    }
  }

  const res = validateResolvedCondition(condition, values, dictionary)
  debugConditions.log(
    `Condition result ${condition.selector}`,
    condition,
    res,
    values
  )

  if (!res.result && id && res.failurePath?.length) {
    return {
      ...res,
      failurePath: [{ ...res.failurePath[0], id }, ...res.failurePath.slice(1)],
    }
  }

  return res
}

export default validateCondition
